import type { ReactNode } from "react";
import { Group, Stack, Text } from "@mantine/core";
import { NextGenBrandIcon } from "./NextGenBrandIcon";

export interface NextGenBrandProps {
  name?: string;
  subtitle?: ReactNode;
  imageSize?: number;
  compact?: boolean;
  monochrome?: "light" | "dark";
}

/**
 * Customer-facing NextGen Diagnostics lockup.
 *
 * Text intentionally remains live HTML so it is accessible, responsive, and
 * can be replaced when the EvoLys deployment is branded for another lab.
 */
export function NextGenBrand({
  name = "NextGen Diagnostics",
  subtitle,
  imageSize = 40,
  compact = false,
  monochrome,
}: NextGenBrandProps) {
  const isNextGen = name.trim().toLowerCase() === "nextgen diagnostics";
  const foreground =
    monochrome === "light"
      ? "#ffffff"
      : monochrome === "dark"
        ? "#0b1f3a"
        : undefined;

  return (
    <Group gap={compact ? 7 : "sm"} wrap="nowrap">
      <NextGenBrandIcon size={imageSize} monochrome={monochrome} />
      <Stack gap={0}>
        {isNextGen ? (
          <Group gap={5} wrap="nowrap" align="baseline">
            <Text
              component="span"
              ff="Montserrat, sans-serif"
              fw={700}
              size={compact ? "md" : "lg"}
              lh={1.05}
              c={foreground ?? "var(--evolys-brand-ink)"}
            >
              Next
              <Text
                component="span"
                inherit
                c={foreground ?? "var(--evolys-brand-primary)"}
              >
                Gen
              </Text>
            </Text>
            <Text
              component="span"
              ff="Montserrat, sans-serif"
              fw={700}
              size={compact ? "xs" : "sm"}
              lh={1}
              c={foreground ?? "var(--evolys-brand-ink)"}
              tt="uppercase"
              style={{ letterSpacing: "0.12em" }}
            >
              Diagnostics
            </Text>
          </Group>
        ) : (
          <Text
            fw={700}
            size={compact ? "md" : "lg"}
            lh={1.15}
            c={foreground}
          >
            {name}
          </Text>
        )}
        {subtitle ? (
          <Text
            size="xs"
            c={monochrome === "light" ? "rgba(255,255,255,0.72)" : "dimmed"}
            lh={1.25}
          >
            {subtitle}
          </Text>
        ) : null}
      </Stack>
    </Group>
  );
}
